/**
 * "Sign in with Microsoft" (DESIGN.md "The Microsoft Button Rule"):
 * Microsoft's own light button in Carbon's square 48px shape, full column
 * width. White with near-black text, the four-square logo (21px: four 10px
 * squares, 1px apart) leading a centered 15px/600 label; a 1px #8c8c8c
 * border in the light theme; hover #f2f2f2, press #e6e6e6; a 2px focus
 * outline at a 2px offset. While busy a spinner replaces the logo and the
 * label reads "Opening Microsoft sign-in".
 *
 * A third-party brand control: its colors are Microsoft's, not tokens, so
 * this file is on the §8.3 literal-color allowlist. The logo carries
 * `data-ms-logo` for the harness offPalette exemption.
 */
import clsx from 'clsx'
import { Spinner } from './Spinner'
import type { MicrosoftSignInButtonProps } from './types'

const LOGO = ['#f25022', '#7fba00', '#00a4ef', '#ffb900']
const BUSY_LABEL = 'Opening Microsoft sign-in'

export function MicrosoftSignInButton({
  onClick,
  busy = false,
  label = 'Sign in with Microsoft',
  disabled = false,
  type = 'button',
  className,
}: MicrosoftSignInButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={clsx(
        'flex h-12 w-full items-center justify-center gap-3 border border-transparent bg-[#ffffff] px-4',
        'text-[15px] font-semibold leading-[1.29] tracking-normal text-[#161616]',
        'transition-colors duration-70 ease-carbon hover:bg-[#f2f2f2] active:bg-[#e6e6e6] light:border-[#8c8c8c]',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--focus)]',
        busy ? 'cursor-progress' : 'disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
    >
      {busy ? (
        <Spinner size={16} />
      ) : (
        <span data-ms-logo="" aria-hidden="true" className="grid flex-none grid-cols-[10px_10px] gap-px">
          {LOGO.map(color => (
            <span key={color} className="h-2.5 w-2.5" style={{ backgroundColor: color }} />
          ))}
        </span>
      )}
      <span>{busy ? BUSY_LABEL : label}</span>
    </button>
  )
}

export default MicrosoftSignInButton
